/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch } from '../../../store/rootStore'
import AuthSlice, { DigitalFarmerAccount, selectAllFarmerAccounts } from '../storage'
import { selectCurrentCart } from '../../cart/storage/cart.slice'

interface AccountSwitcherContextType {
  accounts: DigitalFarmerAccount[]
  filteredAccounts: DigitalFarmerAccount[]
  recentViews: DigitalFarmerAccount[]
  searchText: string
  searchEnabled: boolean
  openAlertModal: boolean
  switchId: string | undefined
  setSearchText: (text: string) => void
  handleOnSearch: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  onChange: (event: React.SyntheticEvent<Element, Event> | undefined, value: string) => boolean
  setOpenAlertModal: (open: boolean) => void
}

const AccountSwitcherContext = createContext<AccountSwitcherContextType | undefined>(undefined)

export const AccountSwitcherProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const farmlandsAccounts = useSelector(selectAllFarmerAccounts)
  const cart = useSelector(selectCurrentCart)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const query = new URLSearchParams(location.search)
  const accountNumber = query.get('accountNumber')

  const [openAlertModal, setOpenAlertModal] = useState(false)
  const [switchId, setSwitchId] = useState<string | undefined>(undefined)
  const [filteredAccounts, setFilteredAccounts] = useState<DigitalFarmerAccount[]>([])
  const [searchText, setSearchText] = useState<string>('')
  const [searchEnabled, setSearchEnabled] = useState<boolean>(false)

  const accounts = useMemo(() => {
    const accounts: DigitalFarmerAccount[] = []
    if (farmlandsAccounts) {
      const keys = Object.keys(farmlandsAccounts.accounts)
      keys.forEach((key) => accounts.push(farmlandsAccounts.accounts[key]))
    }
    return accounts.sort((a, b) => a.accountName.localeCompare(b.accountName))
  }, [farmlandsAccounts])

  useEffect(() => {
    setFilteredAccounts([...accounts].sort((a, b) => a.accountName.localeCompare(b.accountName)))
    setSearchEnabled(accounts.length >= 10)
  }, [accounts])

  const removeAccountNumberFromQueryParams = () => {
    // Remove the 'accountNumber' parameter
    query.delete('accountNumber')

    // Convert the searchParams back to a string
    const newSearch = query.toString()

    // Use navigate with replace to update the URL without reloading or re-rendering
    navigate(`${location.pathname}?${newSearch}`, { replace: true })
  }

  useEffect(() => {
    if (accountNumber && accounts.length) {
      const matchedAccount = accounts.find((account) => account.accountNumber === accountNumber)
      if (matchedAccount) {
        dispatch(AuthSlice.actions.setSelectedFarmerAccount(accountNumber))
      }
      // Leave exposed? Or add another use effect to sync ?accountNumber=  with account switcher?
      removeAccountNumberFromQueryParams()
    }
  }, [accountNumber, accounts.length])

  const recentViews = useMemo(
    () =>
      accounts
        .filter((account) => !!account.lastViewedAt)
        .sort((a, b) => new Date(b.lastViewedAt!).getTime() - new Date(a.lastViewedAt!).getTime())
        .slice(0, 3),
    [accounts],
  )

  const handleOnSearch = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputValue = event.target.value
    setSearchText(inputValue)
    const filtered = accounts.filter(
      (account) =>
        account.accountNumber.includes(inputValue.toString()) ||
        account.accountName.toLowerCase().includes(inputValue.toLowerCase()),
    )
    setFilteredAccounts(filtered)
  }

  const onChange = (event: React.SyntheticEvent<Element, Event> | undefined, value: string) => {
    if (cart && cart.items.length > 0) {
      event?.preventDefault()
      event?.stopPropagation()
      setSwitchId(accounts.find((e) => e.accountNumber === value)?.accountNumber)
      setOpenAlertModal(true)
      return false
    } else {
      const id = accounts.find((e) => e.accountNumber === value)?.accountNumber
      if (id) {
        dispatch(AuthSlice.actions.setSelectedFarmerAccount(id))
      }

      return true
    }
  }

  return (
    <AccountSwitcherContext.Provider
      value={{
        accounts,
        filteredAccounts,
        recentViews,
        searchText,
        searchEnabled,
        openAlertModal,
        switchId,
        setSearchText,
        handleOnSearch,
        onChange,
        setOpenAlertModal,
      }}
    >
      {children}
    </AccountSwitcherContext.Provider>
  )
}

export const useAccountSwitcher = (): AccountSwitcherContextType => {
  const context = useContext(AccountSwitcherContext)
  if (!context) {
    throw new Error('useAccountSwitcher must be used within an AccountSwitcherProvider')
  }
  return context
}
